// src/onboarding/copy.js
//
// OUR sentence for every refusal of this workflow, keyed on `err.code` / `err.status`.
// ⛔ Never the server's own message text (English), never a `details` string. A 5xx carrying a request id
// shows that reference. Each helper takes `t` bound to the `onboarding` namespace.
// Billing refusals (402 / 503 LOOKUP_FAILED / TENANT_RETIRED) are NOT worded here: the caller
// routes them through runRefusal.refusalFromError + RunRefusalNotice first.

function fallback(t, err) {
  if (err && typeof err.status === 'number' && err.status >= 500 && err.requestId) {
    return t('errors.server', { ref: err.requestId });
  }
  if (!err || err.status == null) return t('errors.network');
  return t('errors.generic', { code: err.code || err.status });
}

/** The rename the form sends first. */
export function renameErrorCopy(t, err) {
  if (err?.code === 'INVALID_DISPLAY_NAME') return t('errors.invalidName');
  if (err?.code === 'TENANT_ADMIN_REQUIRED') return t('errors.notAdmin');
  return fallback(t, err);
}

export function brandingErrorCopy(t, err) {
  if (err?.code === 'BRANDING_INVALID_FILE_TYPE' || err?.code === 'BRANDING_UNKNOWN_SLOT') return t('errors.brandingType');
  if (err?.code === 'TENANT_ADMIN_REQUIRED') return t('errors.notAdmin');
  return fallback(t, err);
}

/**
 * v192 — why the last start stopped AFTER its 202 (`last_start_error.code`), as a short reason that
 * fits "L'ultimo avvio … non è riuscito: {reason}". Past tense on purpose: the line can be read long
 * after the fact (the error stays until a newer run exists), when credits or the plan may have changed.
 */
const START_REASON = {
  CREDITS_EXHAUSTED: 'errors.startReason.CREDITS_EXHAUSTED',
  TRIAL_EXPIRED: 'errors.startReason.TRIAL_EXPIRED',
  NO_PLAN: 'errors.startReason.NO_PLAN',
  NO_ALLOWANCE: 'errors.startReason.NO_ALLOWANCE',
  LOOKUP_FAILED: 'errors.startReason.LOOKUP_FAILED',
  TENANT_RETIRED: 'errors.startReason.TENANT_RETIRED',
  ALREADY_PERSONALIZED: 'errors.alreadyPersonalized',
  EXEMPT_TENANT: 'errors.workspaceProblem',
  NO_BRAND_GUIDELINES: 'errors.workspaceProblem',
  HUMAN_AUTHORED: 'errors.workspaceProblem',
  START_FAILED: 'errors.startReason.START_FAILED',
  // The backend re-runs the POST's own gates before it claims the run, so these two can arrive here too.
  NOTHING_TO_DO: 'errors.startReason.NOTHING_TO_DO',
  DOCUMENT_NOT_FOUND: 'errors.startReason.DOCUMENT_NOT_FOUND',
};

export function startErrorReason(t, code) {
  const key = START_REASON[code];
  return key ? t(key) : t('errors.generic', { code: code || '—' });
}

/** POST /personalization, for the codes that are NOT billing refusals and NOT navigations. */
export function formErrorCopy(t, err) {
  switch (err?.code) {
    case 'NOTHING_TO_DO': return t('errors.nothingToDo');
    // The form shows this one under the website field; worded here too for any other caller.
    case 'WEBSITE_UNRESOLVABLE': return t('errors.websiteUnresolvable');
    case 'DOCUMENT_NOT_FOUND': {
      // The user's OWN filenames (they come from this form) — never server prose.
      const files = Array.isArray(err?.details?.documents) ? err.details.documents.filter(Boolean) : [];
      return files.length
        ? t('errors.documentNotFound', { files: files.join(', ') })
        : t('errors.documentNotFoundGeneric');
    }
    case 'BRIEF_VALIDATION_ERROR': return t('errors.invalidForm');
    case 'ALREADY_PERSONALIZED': return t('errors.alreadyPersonalized');
    case 'EXEMPT_TENANT':
    case 'NO_BRAND_GUIDELINES':
    case 'HUMAN_AUTHORED': return t('errors.workspaceProblem');
    case 'TENANT_ADMIN_REQUIRED': return t('errors.notAdmin');
    default: return fallback(t, err);
  }
}
